import { useState } from 'react';
import { AlertCircle, CheckCircle, Loader, CreditCard, RefreshCw } from 'lucide-react';
import { getCsrfToken } from '../utils/csrfToken';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function AdminResyncPanel() {
  // Resync Payments state
  const [personIds, setPersonIds] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // Recalculate Credits state
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const [creditResults, setCreditResults] = useState(null);

  const handleResyncPayments = async () => {
    if (!personIds.trim()) {
      setError('Please enter at least one person ID');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = await getCsrfToken();
      
      const response = await fetch(`${API_URL}/api/admin/resync-payments`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ 
          personIds: personIds.split(',').map(id => id.trim()) 
        })
      });

      if (!response.ok) {
        throw new Error('Failed to resync payments');
      }

      const data = await response.json();
      setResults(data);
      setPersonIds('');
    } catch (err) {
      setError(err.message || 'Resync failed');
      console.error('Resync error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateCredits = async () => {
    setCreditLoading(true);
    setCreditError(null);
    
    try {
      const token = await getCsrfToken();
      
      const response = await fetch(`${API_URL}/api/admin/recalculate-credits`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        }
      });

      if (!response.ok) {
        throw new Error('Failed to recalculate credits');
      }

      const data = await response.json();
      setCreditResults(data);
    } catch (err) {
      setCreditError(err.message || 'Recalculation failed');
      console.error('Credit recalc error:', err);
    } finally {
      setCreditLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* ========== RESYNC PAYMENTS SECTION ========== */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Resync Payments</h2>
          <p className="text-gray-600 text-sm">
            Reset CBA dashboard member info to what is in PCO.
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Use case:</span> useful after PCO merge.
            </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {!results ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Person IDs to Resync
              </label>
              <textarea
                value={personIds}
                onChange={(e) => setPersonIds(e.target.value)}
                placeholder="123456, 789012, 345678"
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 font-mono text-sm"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated list of Planning Center person IDs
              </p>
            </div>

            <button
              onClick={handleResyncPayments}
              disabled={loading || !personIds.trim()}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Resyncing...
                </>
              ) : (
                'Resync Payments'
              )}
            </button>
          </div>
        ) : (
          <div>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-green-900 mb-2">Resync Successful!</p>
                  <div className="space-y-1 text-sm text-green-800">
                    <p><span className="font-semibold">People processed:</span> {results.details?.processed || 0}</p>
                    <p><span className="font-semibold">Payments found:</span> {results.paymentsFound || 0}</p>
                    {results.failedPayments > 0 && (
                      <p><span className="font-semibold">Errors:</span> {results.failedPayments}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {results.details?.errors && results.details.errors.length > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="font-semibold text-yellow-900 mb-2">Failed Payments</p>
                <div className="space-y-2 text-sm text-yellow-800 max-h-48 overflow-y-auto">
                  {results.details.errors.map((err, i) => (
                    <div key={i} className="text-xs">
                      <p><span className="font-semibold">Person {err.personId}:</span> {err.errorMessage}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setResults(null);
                setPersonIds('');
                setError(null);
              }}
              className="w-full bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 font-medium transition-colors"
            >
              Resync Another
            </button>
          </div>
        )}
      </div>

      {/* ========== DIVIDER ========== */}
      <hr className="border-gray-300" />

      {/* ========== RECALCULATE CREDITS SECTION ========== */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Recalculate Credits</h2>
          <p className="text-gray-600 text-sm">
            Recalculate credit balances for all members. 
          </p>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <p className="text-sm text-purple-900">
            <span className="font-semibold">Use case:</span> Only use if absolutely needed. Done nightly.
          </p>
        </div>

        {creditError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-800">{creditError}</p>
            </div>
          </div>
        )}

        {!creditResults ? (
          <button
            onClick={handleRecalculateCredits}
            disabled={creditLoading}
            className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {creditLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Recalculating...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Recalculate All Credits
              </>
            )}
          </button>
        ) : (
          <div>
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-blue-900 mb-2">{creditResults.message}</p>
                  {creditResults.note && (
                    <p className="text-sm text-blue-800 mb-2">{creditResults.note}</p>
                  )}
                  <p className="text-sm text-blue-800">
                    
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setCreditResults(null);
                setCreditError(null);
              }}
              className="w-full bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 font-medium transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminResyncPanel;